import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { Plus, TrendingUp, CheckCircle, Clock4, DollarSign, Ticket as TicketIcon, Plane, AlertCircle, RefreshCw } from 'lucide-react';
import type { AdminOutletContext } from '../types';
import { Client, MOCK_CLIENTS, QuoteRecord, type Flight, type Ticket } from '../types';
import MissingAgentNotice from '../components/MissingAgentNotice';
import { useAgent } from '../context/AgentsContext';
import FlightQuoteModal from '../components/FlightQuoteModal';
import {
  type AgentEmailBackupSmtpConfig,
  loadAgentEmailBackupSettings,
  loadClients,
  saveAgentEmailBackupSettings,
  saveClients,
} from '../lib/skyluxStore';
import { fetchFlights, fetchQuotes, fetchTickets } from '../lib/api';

const OPEN_TICKET = new Set<Ticket['status']>(['open', 'in_progress', 'waiting_customer']);

const AdminOverview = () => {
  const { agentId } = useParams();
  const { globalSearch } = useOutletContext<AdminOutletContext>();
  const agent = useAgent(agentId);
  const [clients, setClients] = useState<Client[]>(() => loadClients(MOCK_CLIENTS));
  const [quotes, setQuotes] = useState<QuoteRecord[]>([]);
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [flights, setFlights] = useState<Flight[]>([]);
  const [quoteClient, setQuoteClient] = useState<Client | null>(null);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [backupSmtp, setBackupSmtp] = useState<AgentEmailBackupSmtpConfig>({
    host: '',
    port: 587,
    secure: false,
    user: '',
    pass: '',
    mailFrom: '',
    replyTo: '',
  });
  const [backupEnabled, setBackupEnabled] = useState(false);
  const [backupSaved, setBackupSaved] = useState(false);
  const [tick, setTick] = useState(0);
  const [apiLoading, setApiLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    const fn = () => {
      setClients(loadClients(MOCK_CLIENTS));
      setTick((t) => t + 1);
    };
    window.addEventListener('skylux-storage', fn);
    return () => window.removeEventListener('skylux-storage', fn);
  }, []);

  useEffect(() => {
    let active = true;
    async function load() {
      setApiLoading(true);
      setApiError(null);
      try {
        const [q, t, f] = await Promise.all([fetchQuotes(), fetchTickets(), fetchFlights()]);
        if (!active) return;
        setQuotes(q);
        setTickets(t);
        setFlights(f);
      } catch (e) {
        if (!active) return;
        setApiError(e instanceof Error ? e.message : 'Could not load API data');
        setQuotes([]);
        setTickets([]);
        setFlights([]);
      } finally {
        if (active) setApiLoading(false);
      }
    }
    void load();
    const interval = window.setInterval(() => void load(), 20000);
    return () => {
      active = false;
      window.clearInterval(interval);
    };
  }, [tick]);

  useEffect(() => {
    if (!agent) return;
    const row = loadAgentEmailBackupSettings(agent.id);
    setBackupSmtp(row.smtp);
    setBackupEnabled(row.enabled);
  }, [agent]);

  const metrics = useMemo(() => {
    const pending = clients.filter((c) => c.status === 'pending').length;
    const unpaidQ = quotes.filter((q) => q.paymentStatus === 'unpaid').length;
    const paidRev = quotes.filter((q) => q.paymentStatus === 'paid').reduce((a, q) => a + q.pricePerPerson, 0);
    const pipe = quotes.filter((q) => q.paymentStatus === 'unpaid').reduce((a, q) => a + q.pricePerPerson, 0);
    const openTickets = tickets.filter((t) => OPEN_TICKET.has(t.status)).length;
    return {
      pending,
      unpaidQ,
      paidRev,
      pipe,
      total: clients.length,
      openTickets,
      flightRows: flights.length,
    };
  }, [clients, quotes, tickets, flights]);

  const recentQuotes = useMemo(() => {
    return [...quotes]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .slice(0, 6);
  }, [quotes]);

  const recentTickets = useMemo(() => {
    return [...tickets]
      .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
      .slice(0, 6);
  }, [tickets]);

  const upsertClient = (c: Client) => {
    setClients((prev) => {
      const idx = prev.findIndex((x) => x.id === c.id);
      const next = idx >= 0 ? prev.map((x) => (x.id === c.id ? c : x)) : [c, ...prev];
      saveClients(next);
      return next;
    });
  };

  const openQuote = (client: Client | null) => {
    setQuoteClient(client);
    setQuoteOpen(true);
  };

  const filteredClients = useMemo(() => {
    const q = globalSearch.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        (c.origin && c.origin.toLowerCase().includes(q)) ||
        (c.destination && c.destination.toLowerCase().includes(q))
    );
  }, [clients, globalSearch]);

  const fmtShort = (iso: string) => {
    try {
      return new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
      return iso;
    }
  };

  if (!agent) {
    return (
      <div className="animate-fade">
        <MissingAgentNotice />
      </div>
    );
  }

  const saveBackup = () => {
    saveAgentEmailBackupSettings(agent.id, { smtp: backupSmtp, enabled: backupEnabled });
    setBackupSaved(true);
    window.setTimeout(() => setBackupSaved(false), 1800);
  };

  const emailLike = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const backupInvalid =
    backupEnabled &&
    (!backupSmtp.host.trim() ||
      !backupSmtp.user.trim() ||
      !backupSmtp.pass.trim() ||
      !backupSmtp.mailFrom.trim() ||
      !emailLike.test(backupSmtp.mailFrom.trim()) ||
      (backupSmtp.replyTo?.trim() ? !emailLike.test(backupSmtp.replyTo.trim()) : false));

  return (
    <div className="animate-fade">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          marginBottom: '2rem',
          flexWrap: 'wrap',
          gap: '1rem',
        }}
      >
        <div>
          <h2 style={{ fontSize: 'clamp(1.5rem, 4vw, 2.25rem)', fontWeight: '800', letterSpacing: '-0.025em', marginBottom: '0.5rem' }}>
            Performance overview
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '1rem', maxWidth: '52ch', lineHeight: 1.5 }}>
            Welcome back,{' '}
            <span style={{ color: 'var(--accent)', fontWeight: '700' }}>{agent.name}</span>{' '}
            <span style={{ color: 'var(--text-muted)' }}>({agent.nickname})</span>. Quotes, tickets, and flight inventory sync from the API every 20s.
          </p>
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <button
            type="button"
            onClick={() => setTick((t) => t + 1)}
            disabled={apiLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.45rem',
              padding: '0.6rem 1rem',
              borderRadius: '0.75rem',
              border: '1px solid var(--border)',
              background: 'white',
              fontWeight: '600',
              fontSize: '0.875rem',
              color: 'var(--text-muted)',
              cursor: apiLoading ? 'wait' : 'pointer',
            }}
          >
            <RefreshCw size={16} style={{ animation: apiLoading ? 'spin 0.8s linear infinite' : undefined }} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => openQuote(null)}
            style={{
              background: 'var(--accent)',
              color: 'white',
              padding: '0.75rem 1.5rem',
              borderRadius: '0.75rem',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontWeight: '700',
              boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <Plus size={20} /> New quote
          </button>
        </div>
      </div>

      <section
        style={{
          background: 'white',
          borderRadius: '1rem',
          border: '1px solid var(--border)',
          padding: '0.9rem 1rem',
          marginBottom: '1rem',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
          <strong style={{ fontSize: '0.92rem' }}>Backup sender</strong>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              checked={backupEnabled}
              onChange={(e) => setBackupEnabled(e.target.checked)}
            />
            Use backup
          </label>
          <input type="text" value={backupSmtp.host} onChange={(e) => setBackupSmtp((s) => ({ ...s, host: e.target.value }))} placeholder="SMTP host" style={{ minWidth: 180 }} />
          <input type="number" value={backupSmtp.port} onChange={(e) => setBackupSmtp((s) => ({ ...s, port: Number(e.target.value) || 587 }))} placeholder="Port" style={{ width: 90 }} />
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.82rem' }}>
            <input type="checkbox" checked={backupSmtp.secure} onChange={(e) => setBackupSmtp((s) => ({ ...s, secure: e.target.checked }))} />
            Secure
          </label>
          <input type="text" value={backupSmtp.user} onChange={(e) => setBackupSmtp((s) => ({ ...s, user: e.target.value }))} placeholder="SMTP user" style={{ minWidth: 200 }} />
          <input type="password" value={backupSmtp.pass} onChange={(e) => setBackupSmtp((s) => ({ ...s, pass: e.target.value }))} placeholder="SMTP password" style={{ minWidth: 200 }} />
          <input type="email" value={backupSmtp.mailFrom} onChange={(e) => setBackupSmtp((s) => ({ ...s, mailFrom: e.target.value }))} placeholder="From email" style={{ minWidth: 240 }} />
          <input type="email" value={backupSmtp.replyTo || ''} onChange={(e) => setBackupSmtp((s) => ({ ...s, replyTo: e.target.value }))} placeholder="Reply-To (optional)" style={{ minWidth: 240 }} />
          <button
            type="button"
            onClick={saveBackup}
            disabled={backupInvalid}
            style={{
              padding: '0.5rem 0.85rem',
              borderRadius: '0.6rem',
              border: '1px solid var(--border)',
              background: '#f8fafc',
              fontWeight: 700,
            }}
          >
            Save backup
          </button>
          {backupSaved ? <span style={{ fontSize: '0.8rem', color: '#166534' }}>Saved</span> : null}
        </div>
        <p style={{ margin: '0.45rem 0 0', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
          When enabled, emails sent from this agent panel use this backup SMTP config (host/user/pass/mailFrom), similar to server env SMTP.
        </p>
      </section>

      {apiError && (
        <div
          role="alert"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.65rem',
            marginBottom: '1.25rem',
            padding: '0.85rem 1.1rem',
            borderRadius: '0.85rem',
            background: '#fef2f2',
            border: '1px solid #fecaca',
            color: '#991b1b',
            fontSize: '0.875rem',
          }}
        >
          <AlertCircle size={18} aria-hidden />
          {apiError}
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
          gap: '1.25rem',
          marginBottom: '2rem',
        }}
      >
        <StatCard
          icon={<TrendingUp color="#3b82f6" />}
          label="Active clients"
          value={apiLoading ? '—' : String(metrics.total)}
          change="Local CRM"
        />
        <StatCard
          icon={<Clock4 color="#fbbf24" />}
          label="Open leads"
          value={String(metrics.pending)}
          change="Pending status"
        />
        <StatCard
          icon={<TicketIcon color="#0ea5e9" />}
          label="Open tickets"
          value={apiLoading ? '—' : String(metrics.openTickets)}
          change={`${tickets.length} total`}
        />
        <StatCard
          icon={<CheckCircle color="#10b981" />}
          label="Unpaid quotes"
          value={apiLoading ? '—' : String(metrics.unpaidQ)}
          change="Awaiting payment"
        />
        <StatCard
          icon={<DollarSign color="#8b5cf6" />}
          label="Revenue (paid)"
          value={apiLoading ? '—' : `$${metrics.paidRev.toLocaleString()}`}
          change={`Pipeline $${metrics.pipe.toLocaleString()}`}
        />
        <StatCard
          icon={<Plane color="#f97316" />}
          label="Flight inventory"
          value={apiLoading ? '—' : String(metrics.flightRows)}
          change="Saved rows"
        />
      </div>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2rem', marginBottom: '2rem' }}>
        <div
          style={{
            flex: '1 1 420px',
            minWidth: 0,
            background: 'white',
            borderRadius: '1.5rem',
            border: '1px solid var(--border)',
            padding: 'clamp(1.25rem, 3vw, 2rem)',
          }}
        >
          <h3 style={{ fontSize: '1.2rem', fontWeight: '700', marginBottom: '1.25rem' }}>Recent client activity</h3>
          <div style={{ display: 'grid', gap: '1rem' }}>
            {filteredClients.length === 0 ? (
              <p
                style={{
                  padding: '2rem',
                  textAlign: 'center',
                  color: 'var(--text-muted)',
                  border: '1px dashed var(--border)',
                  borderRadius: '1rem',
                }}
              >
                No clients match your search.
              </p>
            ) : (
              filteredClients.map((client) => (
                <div
                  key={client.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '1rem',
                    border: '1px solid #f1f5f9',
                    borderRadius: '1rem',
                    gap: '1rem',
                    flexWrap: 'wrap',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', minWidth: 0 }}>
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '10px',
                        background: '#f8fafc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        flexShrink: 0,
                      }}
                    >
                      {client.name[0] ?? '?'}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <Link
                        to={`/admin/agent/${agentId}/clients/${client.id}`}
                        style={{ fontWeight: '700', color: 'inherit', textDecoration: 'none' }}
                      >
                        {client.name}
                      </Link>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.15rem 0 0' }}>
                        {client.email || 'No email — edit in quote flow'}
                        {client.origin && client.destination ? ` · ${client.origin} → ${client.destination}` : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => openQuote(client)}
                    style={{
                      padding: '0.5rem 1rem',
                      background: '#f1f5f9',
                      borderRadius: '0.5rem',
                      fontSize: '0.8125rem',
                      fontWeight: '700',
                      border: 'none',
                      cursor: 'pointer',
                    }}
                  >
                    Send quote
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        <div
          style={{
            flex: '0 1 340px',
            minWidth: 'min(100%, 280px)',
            background: 'var(--primary)',
            borderRadius: '1.5rem',
            padding: 'clamp(1.25rem, 3vw, 2rem)',
            color: 'white',
            alignSelf: 'flex-start',
          }}
        >
          <h3 style={{ fontSize: '1.125rem', fontWeight: '700', marginBottom: '1rem' }}>Agent workspace</h3>
          <p style={{ fontSize: '0.875rem', opacity: 0.88, lineHeight: 1.65, marginBottom: '1.25rem' }}>
            Quotes open the flight search engine. Client links use <strong>booking.skyluxtravel</strong>; payment uses{' '}
            <strong>payment.skyluxtravel</strong> (on localhost:{' '}
            <code style={{ fontSize: '0.75rem', background: 'rgba(255,255,255,0.12)', padding: '0.1rem 0.35rem', borderRadius: '4px' }}>/booking</code> and{' '}
            <code style={{ fontSize: '0.75rem', background: 'rgba(255,255,255,0.12)', padding: '0.1rem 0.35rem', borderRadius: '4px' }}>/payment</code>
            ).
          </p>
          <div style={{ display: 'grid', gap: '1.5rem' }}>
            <GoalItem label="Client records" current={metrics.total} target={25} color="var(--accent)" />
            <GoalItem label="Open pipeline (quotes)" current={metrics.unpaidQ} target={15} color="var(--success)" />
            <GoalItem
              label="Paid clients"
              current={clients.filter((c) => c.status === 'paid').length}
              target={Math.max(metrics.total, 1)}
              color="var(--gold)"
            />
          </div>
        </div>
      </div>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
          gap: '1.5rem',
        }}
      >
        <Panel
          title="Recent quotes"
          subtitle="Latest from API"
          loading={apiLoading}
          empty={recentQuotes.length === 0}
        >
          {recentQuotes.map((q) => (
            <div
              key={q.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '0.85rem 0',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Link
                  to={`/admin/agent/${agentId}/clients/${q.clientId}`}
                  style={{ fontWeight: '700', color: 'inherit', textDecoration: 'none', fontSize: '0.9rem' }}
                >
                  {q.clientSnapshot.name}
                </Link>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {q.flight.departure} → {q.flight.arrival} · ${q.pricePerPerson.toLocaleString()} pp
                </p>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{fmtShort(q.createdAt)}</p>
              </div>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: '0.65rem',
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '6px',
                  background: q.paymentStatus === 'paid' ? '#dcfce7' : '#fef3c7',
                  color: q.paymentStatus === 'paid' ? '#166534' : '#92400e',
                }}
              >
                {q.paymentStatus}
              </span>
            </div>
          ))}
        </Panel>

        <Panel title="Recent tickets" subtitle="By last update" loading={apiLoading} empty={recentTickets.length === 0}>
          {recentTickets.map((t) => (
            <div
              key={t.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: '0.75rem',
                padding: '0.85rem 0',
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <Link to={`/admin/agent/${agentId}/tickets`} style={{ fontWeight: '700', color: 'inherit', textDecoration: 'none', fontSize: '0.9rem' }}>
                  {t.code} · {t.title}
                </Link>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  {t.clientName} · {t.clientEmail}
                </p>
                <p style={{ margin: '0.2rem 0 0', fontSize: '0.7rem', color: 'var(--text-muted)' }}>{fmtShort(t.updatedAt)}</p>
              </div>
              <span
                style={{
                  flexShrink: 0,
                  fontSize: '0.65rem',
                  fontWeight: '800',
                  textTransform: 'uppercase',
                  padding: '0.2rem 0.5rem',
                  borderRadius: '6px',
                  background: '#e0f2fe',
                  color: '#0369a1',
                }}
              >
                {t.status.replace('_', ' ')}
              </span>
            </div>
          ))}
        </Panel>
      </div>

      <FlightQuoteModal
        open={quoteOpen}
        onClose={() => {
          setQuoteOpen(false);
          setQuoteClient(null);
        }}
        agent={agent}
        client={quoteClient}
        onUpsertClient={upsertClient}
        onAfterQuoteSent={() => setTick((x) => x + 1)}
      />
    </div>
  );
};

function Panel({
  title,
  subtitle,
  children,
  empty,
  loading,
}: {
  title: string;
  subtitle: string;
  children: ReactNode;
  empty: boolean;
  loading?: boolean;
}) {
  return (
    <div style={{ background: 'white', borderRadius: '1.25rem', border: '1px solid var(--border)', padding: '1.25rem 1.5rem' }}>
      <div style={{ marginBottom: '0.5rem' }}>
        <h3 style={{ fontSize: '1.1rem', fontWeight: '700', margin: 0 }}>{title}</h3>
        <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', margin: '0.2rem 0 0' }}>{subtitle}</p>
      </div>
      {loading && empty ? (
        <p style={{ padding: '1.5rem 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</p>
      ) : empty ? (
        <p style={{ padding: '1.5rem 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Nothing yet.</p>
      ) : (
        <div>{children}</div>
      )}
    </div>
  );
}

const StatCard = ({ icon, label, value, change }: { icon: ReactNode; label: string; value: string; change: string }) => (
  <div style={{ background: 'white', padding: '1.35rem', borderRadius: '1.25rem', border: '1px solid var(--border)', minHeight: '120px' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.85rem', alignItems: 'flex-start', gap: '0.5rem' }}>
      <div style={{ padding: '0.5rem', borderRadius: '10px', background: '#f8fafc' }}>{icon}</div>
      <span style={{ fontSize: '0.68rem', fontWeight: '700', color: 'var(--text-muted)', textAlign: 'right', maxWidth: '110px', lineHeight: 1.3 }}>
        {change}
      </span>
    </div>
    <p style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', margin: 0 }}>{label}</p>
    <h4 style={{ fontSize: '1.45rem', fontWeight: '800', margin: '0.25rem 0 0' }}>{value}</h4>
  </div>
);

const GoalItem = ({ label, current, target, color }: { label: string; current: number; target: number; color: string }) => {
  const percent = target > 0 ? Math.min((current / target) * 100, 100) : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
        <span style={{ opacity: 0.85 }}>{label}</span>
        <span style={{ fontWeight: '700' }}>
          {current} / {target}
        </span>
      </div>
      <div style={{ height: '6px', background: 'rgba(255,255,255,0.12)', borderRadius: '3px', overflow: 'hidden' }}>
        <div style={{ height: '100%', background: color, width: `${percent}%`, borderRadius: '3px' }} />
      </div>
    </div>
  );
};

export default AdminOverview;
